import { isAllowedLogoutReason, type LogoutReason } from '@agentteams/realtime-contract';
import { notifyDesktopLogout } from './desktopBridge';
import { isPortalBuild } from './appTarget';
import { readStoredValue, removeStoredValue, writeStoredValue } from './storage';
import { clearWorkspaceContext } from './workspaceContext';
import { clearWorkspaceProjectRoutes } from './workspaceProjectRoute';
import { clearConventionImportDraft } from './conventionImportSession';
import { clearPlanImportDraft } from './planImportSession';
import { clearPersistedQueryClient } from '../providers/queryPersister';

export type SessionMemberLocale = 'en' | 'ko';

export type SessionMember = {
  id: string;
  email: string;
  nickname: string;
  tier: 'FREE' | 'PRO';
  locale: SessionMemberLocale;
  isSystemAdmin: boolean;
  isBetaUser: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AuthSession = {
  token: string;
  refreshToken: string;
  member: SessionMember;
};

type StoredAuthSession = {
  token: string;
  refreshToken?: string;
  member: Omit<SessionMember, 'tier' | 'isSystemAdmin' | 'isBetaUser' | 'locale'> & {
    tier?: string | null;
    isSystemAdmin?: boolean | null;
    isBetaUser?: boolean | null;
    locale?: string | null;
  };
};

export const authSessionStorageKey = 'agentteams-auth-session';
export const authSessionChangedEventName = 'agentteams-auth-session-changed';
const defaultPostLoginPath = '/home';

export { LOGOUT_REASON, isAllowedLogoutReason, type LogoutReason } from '@agentteams/realtime-contract';

// refresh 흐름이 확인한 종료 사유를 handle401Response가 소비할 때까지 임시 보관한다.
// 세션 제한 축출처럼 서버가 확인한 사유만 저장되며, 소비 시 즉시 비워 다른 로그아웃에 새지 않게 한다.
let pendingLogoutReason: LogoutReason | null = null;

export const setPendingLogoutReason = (reason: string | null | undefined) => {
  pendingLogoutReason = isAllowedLogoutReason(reason) ? reason : null;
};

const consumePendingLogoutReason = (): LogoutReason | null => {
  const reason = pendingLogoutReason;
  pendingLogoutReason = null;
  return reason;
};

/**
 * 강제 로그아웃 사유의 탭 간 공유 저장소.
 * 모듈 메모리(`pendingLogoutReason`)는 사유를 감지한 탭에서만 보이므로,
 * 다른 탭도 같은 안내를 보려면 localStorage 레코드로 공유해야 한다.
 * TTL을 두어 이번 축출과 무관한 재방문에 오래된 사유가 재사용되지 않게 한다.
 */
export const logoutReasonStorageKey = 'agentteams-auth-logout-reason';
export const logoutReasonTtlMs = 30_000;

type SharedLogoutReasonRecord = {
  reason: LogoutReason;
  at: number;
};

type RedirectValidationOptions = {
  excludeCli?: boolean;
};

const isBrowser = () => typeof window !== 'undefined';

export const clearSharedLogoutReason = () => {
  // Safari private mode 등 저장소 접근 실패는 무시한다 (안내 표시는 부가 기능).
  removeStoredValue(logoutReasonStorageKey);
};

export const setSharedLogoutReason = (reason: string | null | undefined) => {
  if (!isAllowedLogoutReason(reason)) {
    clearSharedLogoutReason();
    return;
  }
  const record: SharedLogoutReasonRecord = { reason, at: Date.now() };
  // 기록 실패 시 안내 문구만 생략된다.
  writeStoredValue(logoutReasonStorageKey, JSON.stringify(record));
};

/**
 * 유효한 사유는 삭제하지 않고 반환한다.
 * 초기 마운트 경로·구독 경로·여러 탭이 같은 값을 읽어야 하므로 소비형이면 안 된다.
 * 손상·비허용·만료 레코드만 즉시 제거하고 `null`을 반환한다.
 */
export const readSharedLogoutReason = (): LogoutReason | null => {
  // 저장소 차단 환경에서는 공유 사유 없음으로 취급한다 (WEB-R).
  const raw = readStoredValue(logoutReasonStorageKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<SharedLogoutReasonRecord>;
    if (!isAllowedLogoutReason(parsed?.reason) || typeof parsed.at !== 'number') {
      clearSharedLogoutReason();
      return null;
    }
    if (Date.now() - parsed.at >= logoutReasonTtlMs) {
      clearSharedLogoutReason();
      return null;
    }
    return parsed.reason;
  } catch {
    clearSharedLogoutReason();
    return null;
  }
};

/**
 * 세션 변경 알림에 실리는 컨텍스트.
 * `callerHandlesRedirect`는 "호출부가 로그인 이동을 소유한다"는 신호로,
 * 명시적 로그아웃·탈퇴와 Portal 401에서 AuthGuard의 선점 이동을 막는다.
 * source는 교차 탭 로그아웃이 Portal 자동 인가를 유발하지 않도록 구분한다.
 * 모듈 전역 플래그 대신 이벤트에 실어 상태가 남지 않게 한다.
 */
export type AuthSessionChangeContext = {
  callerHandlesRedirect: boolean;
  source: 'same-tab' | 'storage';
};

const notifyAuthSessionChanged = (detail?: Partial<AuthSessionChangeContext>) => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(authSessionChangedEventName, { detail }));
};

export const getAuthSession = (): AuthSession | null => {
  if (!isBrowser()) return null;

  // 저장소 차단 환경에서는 미인증으로 취급한다 (WEB-R).
  const raw = readStoredValue(authSessionStorageKey);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredAuthSession;
    if (!parsed.token || !parsed.member?.id) return null;
    if (!parsed.refreshToken) return null;

    return {
      token: parsed.token,
      refreshToken: parsed.refreshToken,
      member: {
        id: parsed.member.id,
        email: parsed.member.email,
        nickname: parsed.member.nickname,
        tier: parsed.member.tier === 'PRO' ? 'PRO' : 'FREE',
        locale: parsed.member.locale === 'ko' ? 'ko' : 'en',
        isSystemAdmin: parsed.member.isSystemAdmin === true,
        isBetaUser: parsed.member.isBetaUser === true,
        createdAt: parsed.member.createdAt,
        updatedAt: parsed.member.updatedAt,
      },
    };
  } catch {
    return null;
  }
};

export const saveAuthSession = (session: AuthSession) => {
  if (!isBrowser()) return;
  // 세션 저장 성공 = 세션이 건강하다는 신호이므로 남아 있는 강제 로그아웃 사유를 정리한다.
  clearSharedLogoutReason();
  // 저장소 차단 환경에서도 인증 완료 흐름이 throw로 중단되지 않게 best-effort로 저장한다 (WEB-R).
  // 저장이 실패한 환경에서는 세션이 유지되지 않아 AuthGuard가 로그인으로 안내한다.
  writeStoredValue(
    authSessionStorageKey,
    JSON.stringify({
      token: session.token,
      refreshToken: session.refreshToken,
      member: {
        ...session.member,
        tier: session.member.tier === 'PRO' ? 'PRO' : 'FREE',
        locale: session.member.locale === 'ko' ? 'ko' : 'en',
        isSystemAdmin: session.member.isSystemAdmin === true,
        isBetaUser: session.member.isBetaUser === true,
      },
    }),
  );
  notifyAuthSessionChanged();
};

export const clearAuthSession = () => {
  if (!isBrowser()) return;
  // 초대 수락 경로의 세션 무효화는 강제 로그아웃이 아니므로 사유를 남기지 않는다.
  clearSharedLogoutReason();
  removeStoredValue(authSessionStorageKey);
  notifyAuthSessionChanged();
};

export type ClearClientSessionDataOptions = {
  /** 강제 로그아웃(401)처럼 공유 사유를 다음 화면까지 전달해야 할 때만 true. */
  preserveLogoutReason?: boolean;
  /** 명시적 로그아웃·탈퇴처럼 호출부가 이동 목적지를 확정할 때 true. AuthGuard는 이동을 양보한다. */
  callerHandlesRedirect?: boolean;
};

export const clearClientSessionData = async (options?: ClearClientSessionDataOptions) => {
  if (!isBrowser()) return;
  // 다른 탭은 authSessionStorageKey 제거에서 storage 이벤트를 받으므로, 사유 정리는 반드시 그 이전에 끝나야 한다.
  if (!options?.preserveLogoutReason) {
    try {
      clearSharedLogoutReason();
    } catch {
      // 부가 안내 정리 실패가 세션 정리를 막지 않게 한다.
    }
  }
  // 각 정리는 개별 try/catch로 감싼다. 저장소 차단 환경에서 한 정리가 실패해도
  // 나머지 정리와 영속 캐시 삭제·세션 변경 알림이 반드시 실행되게 한다 (WEB-R).
  const cleanups: Array<() => void> = [
    () => removeStoredValue(authSessionStorageKey),
    () => clearWorkspaceContext(),
    () => clearWorkspaceProjectRoutes(),
    () => clearConventionImportDraft(),
    // clearPlanImportDraft removes both the current `plan-import-draft` key and the
    // legacy `task-import-draft` key.
    () => clearPlanImportDraft(),
  ];
  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch {
      // 한 정리 실패가 로그아웃·리다이렉트 흐름을 중단시키지 않게 한다.
    }
  }
  try {
    await clearPersistedQueryClient();
  } catch {
    // 영속 캐시 정리 실패해도 세션 변경 알림은 반드시 보낸다.
  }
  notifyAuthSessionChanged({ callerHandlesRedirect: options?.callerHandlesRedirect === true });
};

export const getAuthToken = () => getAuthSession()?.token ?? null;

const decodeBase64Url = (input: string): string | null => {
  try {
    let normalized = input.replace(/-/g, '+').replace(/_/g, '/');
    const remainder = normalized.length % 4;
    if (remainder === 2) normalized += '==';
    else if (remainder === 3) normalized += '=';
    else if (remainder === 1) return null;

    if (typeof atob !== 'function') return null;
    const binary = atob(normalized);
    if (typeof TextDecoder === 'function') {
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder().decode(bytes);
    }
    return binary;
  } catch {
    return null;
  }
};

export const decodeJwtPayload = (token: string): { exp?: number } | null => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payloadJson = decodeBase64Url(parts[1]);
  if (!payloadJson) return null;
  try {
    const parsed = JSON.parse(payloadJson) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as { exp?: number };
    }
    return null;
  } catch {
    return null;
  }
};

export const getAccessTokenExpiryMs = (token: string | null | undefined): number | null => {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
};

export const isAccessTokenExpired = (token: string | null | undefined, skewMs = 30_000): boolean => {
  const expiryMs = getAccessTokenExpiryMs(token);
  if (expiryMs === null) return false;
  return Date.now() + skewMs >= expiryMs;
};

/** 2번째 인자는 선택적 소비이므로 기존 1-인자 리스너는 그대로 동작한다. */
export type AuthSessionListener = (session: AuthSession | null, context: AuthSessionChangeContext) => void;

export const subscribeAuthSession = (listener: AuthSessionListener): (() => void) => {
  if (!isBrowser()) return () => {};

  const notify = (context: AuthSessionChangeContext) => {
    listener(getAuthSession(), context);
  };

  const handleSameTab = (event: Event) => {
    // detail이 없는 dispatch(new Event(...))는 소유권 없음으로 해석한다.
    const detail = (event as CustomEvent<Partial<AuthSessionChangeContext> | undefined>).detail;
    notify({ callerHandlesRedirect: detail?.callerHandlesRedirect === true, source: 'same-tab' });
  };

  const handleStorage = (event: StorageEvent) => {
    // event.key === null → localStorage.clear() (all keys wiped)
    if (event.key !== null && event.key !== authSessionStorageKey) return;
    // 다른 탭은 이 탭의 이동 소유권과 무관하므로 항상 스스로 이동해야 한다.
    notify({ callerHandlesRedirect: false, source: 'storage' });
  };

  window.addEventListener(authSessionChangedEventName, handleSameTab);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(authSessionChangedEventName, handleSameTab);
    window.removeEventListener('storage', handleStorage);
  };
};

const isInternalPath = (path: string) => {
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  return true;
};

/**
 * Portal 핸드오프 인가 화면 경로. `/auth` 하위지만 로그인 후 복귀가 필요한 단일 예외다.
 * 이 화면은 인가 API 호출 후 Portal로 떠나 머물지 않으므로 로그인 루프에 빠지지 않는다.
 * 쿼리까지 포함한 전체 경로가 넘어오므로 `?` 경계로만 인정한다.
 */
const PORTAL_AUTHORIZE_PATH = '/auth/portal-authorize';

export const isPortalAuthorizePath = (path: string) =>
  path === PORTAL_AUTHORIZE_PATH || path.startsWith(`${PORTAL_AUTHORIZE_PATH}?`);

export const isSafeRedirectPath = (path: string | null | undefined, options?: RedirectValidationOptions) => {
  if (!path) return false;
  if (!isInternalPath(path)) return false;
  if (isPortalAuthorizePath(path)) return true;
  if (path.startsWith('/auth')) return false;
  if (options?.excludeCli && path.startsWith('/cli')) return false;
  return true;
};

export const resolvePostLoginRedirect = (redirectPath: string | null | undefined) => {
  if (!isSafeRedirectPath(redirectPath)) {
    return defaultPostLoginPath;
  }

  return redirectPath ?? defaultPostLoginPath;
};

export const buildLoginRedirectUrl = (
  currentPath: string,
  options?: RedirectValidationOptions,
  reason?: string | null,
) => {
  const params = new URLSearchParams();
  if (isSafeRedirectPath(currentPath, options)) {
    params.set('redirect', currentPath);
  }
  if (isAllowedLogoutReason(reason)) {
    params.set('reason', reason);
  }

  const query = params.toString();
  return query ? `/auth/login?${query}` : '/auth/login';
};

/**
 * Handle 401 Unauthorized response
 * - Clears auth session and persisted client data
 * - Redirects to login page with full page reload
 *
 * EDGE CASE: If called during active optimistic mutation rollback,
 * redirect may interrupt rollback. Current implementation prioritizes
 * clearing user data before navigating away.
 */
export const handle401Response = () => {
  // 겹친 401이나 다른 탭의 후속 401도 같은 강제 로그아웃 세대에 속하므로,
  // 로컬 pending 사유가 이미 소비되었으면 TTL 안의 공유 사유를 재사용한다.
  const reason = consumePendingLogoutReason() ?? readSharedLogoutReason();
  // 다른 탭이 세션 삭제를 관찰하기 전에 기록해야 한다. 유효한 사유가 없는 일반 401은 만료·손상 값을 제거한다.
  setSharedLogoutReason(reason);
  const currentPath = window.location.pathname + window.location.search;
  const loginRedirectUrl = buildLoginRedirectUrl(currentPath, { excludeCli: true }, reason);
  // Full page reload to clear all cached state (not router.push)
  void clearClientSessionData({ preserveLogoutReason: true, callerHandlesRedirect: isPortalBuild }).finally(() => {
    if (notifyDesktopLogout(reason)) return;
    // Portal은 로그인 화면이 없다. portalAuth가 이 모듈을 import하므로 순환을 피하려고 지연 로드한다.
    if (isPortalBuild) {
      void import('./portalAuth').then(({ startPortalAuthorize }) => startPortalAuthorize(currentPath));
      return;
    }
    window.location.href = loginRedirectUrl;
  });
};
